import React, { useState, useRef, useEffect, useCallback } from 'react';
import BoardButton from '../BoardButton';
import { calculateGameScore, formatOperatorSymbol, getBoardDifficulty, getBoardNavigation, getNewNumber, getNumHistoryFromSolution, getOperationHistoryFromSolution, progressCheck, replayActionHistory, convertMillisToSeconds } from '../../helper/helperFunctions';
import { main_background_color } from '../../constants';
import './styles/Game.css';
import { getGameDataProperty, getPlayedBoardNumbers, setCurrentGameData } from '../../services/state';
import { submitEventData } from '../../services/api';
import HowToPlay from './HowToPlay';
import ArchivedBoardModal from './ArchivedBoardModal';
import LimitReachedModal from './LimitReachedModal';
import { log } from '../../helper/logger';
import { useShowTimerSetting } from '../../helper/useShowTimerPreference';
import { hasLimitRuleBeenExplained } from '../../helper/limitWarningSession';

const trackAnalyticsEvent = (eventName, parameters = {}) => {
    if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, parameters);
    }
};

const formatOperatorLabel = (operator) => {
    const symbol = formatOperatorSymbol(operator);
    return (
        <>
            <span className='operator-glyph'>{symbol[0]}</span>
            {symbol.slice(1)}
            {'\u2009'}
        </>
    );
};
const confettiPieces = Array.from({ length: 45 }, (_, index) => index);

const Game = ({ basicUserData, currentBoardData, latestBoardNum, onNavigateToBoard, isArchived, archiveMenuOpen, skipHowToPlay, gameDone, setGameDone, setShowEndModal, showEndModal, isTutorial }) => {
    // console.log('currentBoardData:', currentBoardData);
    const { boardNum, targetNums, operators, solution_length } = currentBoardData;
    const limit = solution_length || 0;
    const difficulty = getBoardDifficulty({ operators, solution_length });

    const scrollBoxRef = useRef(null); // Create a ref for the scrollable box
    const buttonContainerRef = useRef(null);
    const [buttonContainerHeight, setButtonContainerHeight] = useState(0);
    // Matches the mobile breakpoint already used for .button-container in Game.css
    const [isMobileViewport, setIsMobileViewport] = useState(() => window.matchMedia('(max-width: 768px)').matches);

    const [operationHistory, setOperationsHistory] = useState([]);
    const [numHistory, setNumHistory] = useState([targetNums[0]]);
    const [numPermHistory, setNumPermHistory] = useState([]);
    const [actionHistory, setActionHistory] = useState([]);
    const [targetProgressNum, setTargetProgressNum] = useState(1);

    const startTimeRef = useRef(null);
    const [isTiming, setIsTiming] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const [showHowToPlay, setShowHowToPlay] = useState(!skipHowToPlay);
    const [elapsedMs, setElapsedMs] = useState(0);
    const [warningMessage, setWarningMessage] = useState('');
    const [keyboardFlash, setKeyboardFlash] = useState(null);
    const [showConfetti, setShowConfetti] = useState(false);
    const [showLimitReachedModal, setShowLimitReachedModal] = useState(false);
    const firstOperatorTrackedRef = useRef(false);
    const undoTrackedRef = useRef(false);
    const resetTrackedRef = useRef(false);
    const completedRef = useRef(gameDone);
    const playerFinishedRef = useRef(null);

    useEffect(() => {
        completedRef.current = gameDone;
    }, [gameDone]);

    useEffect(() => {
        const handlePageHide = () => {
            if (gameStarted && !completedRef.current) {
                trackAnalyticsEvent('board_abandoned', { board_number: boardNum });
            }
        };

        window.addEventListener('pagehide', handlePageHide);
        return () => window.removeEventListener('pagehide', handlePageHide);
    }, [boardNum, gameStarted]);

    useEffect(() => {
        if (!keyboardFlash) return undefined;

        const timeoutId = window.setTimeout(() => setKeyboardFlash(null), 180);
        return () => window.clearTimeout(timeoutId);
    }, [keyboardFlash]);

    useEffect(() => {
        if (!showConfetti) return undefined;

        const timeoutId = window.setTimeout(() => setShowConfetti(false), 2500);
        return () => window.clearTimeout(timeoutId);
    }, [showConfetti]);

    useEffect(() => {
        if (archiveMenuOpen) {
            setShowHowToPlay(false);
        } else if (!gameStarted && !gameDone) {
            setShowHowToPlay(true);
        }
    }, [archiveMenuOpen, gameDone, gameStarted]);

    const reachedLimit = !gameDone && operationHistory.length >= limit;
    const { previous, next } = getBoardNavigation(boardNum, latestBoardNum, getPlayedBoardNumbers());

    // Once the player has seen the Out of Moves modal this session, later limit hits
    // get a quick inline nudge instead - concise progress feedback if they made any,
    // otherwise the same plain heads-up, but never the modal again.
    const getLimitWarningText = useCallback(() => {
        const remaining = targetNums.length - targetProgressNum;
        const reachedAnyTargets = remaining < targetNums.length - 1;
        return reachedAnyTargets
            ? `Limit reached. Only ${remaining} target${remaining === 1 ? '' : 's'} left!`
            : 'You have reached the operation limit.';
    }, [targetNums.length, targetProgressNum]);

    // Pop up the moment the limit is hit, rather than waiting for the player to try
    // (and get blocked on) another move - but only the first time this session.
    useEffect(() => {
        if (!reachedLimit) return;
        if (hasLimitRuleBeenExplained()) {
            setWarningMessage(getLimitWarningText());
        } else {
            setShowLimitReachedModal(true);
        }
    }, [reachedLimit, getLimitWarningText]);

    const handleBoardNavigation = async (targetBoardNum) => {
        if (!targetBoardNum || typeof onNavigateToBoard !== 'function') return;
        await onNavigateToBoard(targetBoardNum);
    };

    const functionsButtons = [
        { title: 'Undo', func: () => handleUndoClick() },
        { title: 'Clear', func: () => clearGame() },
    ];

    useEffect(() => {
        if (!gameDone) return; // otherwise: do not auto-start on mount — wait for user to press Start

        if (Array.isArray(currentBoardData?.solution)) {
            setNumHistory(getNumHistoryFromSolution(targetNums, operators, currentBoardData.solution));
            setOperationsHistory(getOperationHistoryFromSolution(operators, currentBoardData.solution));
            setTargetProgressNum(currentBoardData.solution.length);
            return;
        }

        // No official solution from the backend (e.g. after a page reload) — replay the
        // player's own persisted action log to rebuild the exact path that won the board.
        const finalOperators = replayActionHistory(getGameDataProperty(boardNum, 'action_history'));
        if (finalOperators.length > 0) {
            const rebuiltNumHistory = [targetNums[0]];
            finalOperators.forEach((operator) => {
                rebuiltNumHistory.push(getNewNumber(rebuiltNumHistory[rebuiltNumHistory.length - 1], operator));
            });
            setNumHistory(rebuiltNumHistory);
            setOperationsHistory(finalOperators);
            setTargetProgressNum(finalOperators.length);
            return;
        }

        setTargetProgressNum(targetNums.length);
    }, [gameDone, boardNum, targetNums, operators, currentBoardData?.solution, currentBoardData?.solution_length]);

    // Scroll to the bottom whenever new content is added
    useEffect(() => {
        if (scrollBoxRef.current) {
            scrollBoxRef.current.scrollTo({
                top: scrollBoxRef.current.scrollHeight,
                behavior: 'smooth' // Smooth scrolling
            });
        }
    }, [operationHistory]);

    // Keep a bottom spacer in sync with the button row's own height, so the page
    // reserves as much room below the buttons as the buttons themselves take up
    // (the button row's height itself varies - it's a fixed vh on larger screens
    // but shrinks to fit its content via media queries on small ones).
    useEffect(() => {
        const node = buttonContainerRef.current;
        if (!node || typeof ResizeObserver === 'undefined') return undefined;

        const observer = new ResizeObserver(() => {
            // Re-measure via getBoundingClientRect (border-box, matches what's
            // actually reserved on screen) rather than the entry's contentRect,
            // which excludes the button row's own padding.
            setButtonContainerHeight(node.getBoundingClientRect().height);
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    // The bottom buffer is mobile-only - track the same breakpoint live so it
    // appears/disappears if the window is resized across it.
    useEffect(() => {
        const mql = window.matchMedia('(max-width: 768px)');
        const handleChange = (event) => setIsMobileViewport(event.matches);
        mql.addEventListener('change', handleChange);
        return () => mql.removeEventListener('change', handleChange);
    }, []);

    useEffect(() => {
        if (!warningMessage) return undefined;

        const timeoutId = window.setTimeout(() => {
            setWarningMessage('');
        }, 4000);

        return () => window.clearTimeout(timeoutId);
    }, [warningMessage]);

    // Live timer updater
    useEffect(() => {
        if (!isTiming) return undefined;
        const id = setInterval(() => {
            if (startTimeRef.current) {
                const seconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
                setElapsedMs(seconds * 1000);
            }
        }, 1000);
        return () => clearInterval(id);
    }, [isTiming]);

    const updateGameHistory = useCallback((nextNumHistory, nextOperationHistory, nextNumPermHistory = numPermHistory, nextActionHistory = actionHistory) => {
        setNumHistory(nextNumHistory);
        setOperationsHistory(nextOperationHistory);
        setNumPermHistory(nextNumPermHistory);
        setActionHistory(nextActionHistory);

        const progress = progressCheck(targetNums, nextNumHistory);
        setTargetProgressNum(progress);
        return progress;
    }, [actionHistory, numPermHistory, targetNums]);

    // Function to handle the operator click
    const handleOperatorClick = useCallback((operator) => {
        // Do not allow moves before the game is started
        if (!gameStarted) return;

        if (!firstOperatorTrackedRef.current) {
            firstOperatorTrackedRef.current = true;
            trackAnalyticsEvent('first_operator_click', { board_number: boardNum });
        }

        // return if the operationHistory history length is greater than the limit
        if (operationHistory.length >= limit) {
            setWarningMessage(hasLimitRuleBeenExplained() ? getLimitWarningText() : 'You have reached the operation limit!');
            return;
        }

        const currentNumber = numHistory[numHistory.length - 1];
        const operatorNumber = (operator.startsWith('+') || operator.startsWith('-'))
            ? parseInt(operator, 10)
            : parseInt(operator.slice(1), 10);

        if (operator.startsWith('/')) {
            if (operatorNumber === 0) {
                setWarningMessage('Cannot divide by zero');
                return;
            }

            if (!Number.isInteger(currentNumber / operatorNumber)) {
                setWarningMessage('Division must result in a whole number');
                return;
            }
        }

        const newNumber = getNewNumber(currentNumber, operator);
        if (typeof newNumber !== 'number') {
            setWarningMessage('Invalid operation');
            return;
        }

        setWarningMessage('');

        const newNumHistory = [...numHistory, newNumber];
        const newNumPermHistory = [...numPermHistory, operator];
        const newActionHistory = [...actionHistory, operator];
        const progress = updateGameHistory(
            newNumHistory,
            [...operationHistory, operator],
            newNumPermHistory,
            newActionHistory
        );

        if (progress === targetNums.length) {
            playerFinishedRef.current?.(newNumPermHistory, newActionHistory);
        }
    }, [actionHistory, boardNum, gameStarted, getLimitWarningText, limit, numHistory, numPermHistory, operationHistory, targetNums, updateGameHistory]);

    const handleStart = () => {
        if (gameStarted) return;
        trackAnalyticsEvent('game_session_start', { board_number: boardNum });
        startTimeRef.current = Date.now();
        setIsTiming(true);
        setGameStarted(true);
        setShowHowToPlay(false);
        setElapsedMs(0);
        log('Timer started at', new Date(startTimeRef.current).toISOString());
    }

    const playerFinished = async (newNumPermHistory, finishedActionHistory) => {
        completedRef.current = true;
        setShowConfetti(true);
        setGameDone(true);
        log('Final permanent num history:', newNumPermHistory);
        const timeTaken = getTimeTaken();
        // stop live timer
        setIsTiming(false);
        setElapsedMs(timeTaken);
        startTimeRef.current = null;
        log('Time taken (ms):', timeTaken, 'Formatted:', convertMillisToSeconds(timeTaken));
        const gameScore = getGameScore(timeTaken, newNumPermHistory);

        if (isTutorial) {
            // Enough persisted (under the non-numeric 'tutorial' key, see getPlayedBoardNumbers)
            // for the EndModal to show a real score/time/history - but no backend
            // submission and no board-navigation data, since it's not a real board.
            trackAnalyticsEvent('tutorial_completed', { time_seconds: timeTaken / 1000 });
            setCurrentGameData(boardNum, 'timeTaken', timeTaken);
            setCurrentGameData(boardNum, 'action_history', finishedActionHistory || actionHistory);
            setShowEndModal(true);
            return;
        }

        trackAnalyticsEvent('board_completed', {
            board_number: boardNum,
            score: gameScore,
            time_seconds: timeTaken / 1000
        });

        // Set the current game data (persist before showing modal)
        setCurrentGameData(boardNum, 'timeTaken', timeTaken)
        // persist action history and other details for sharing
        setCurrentGameData(boardNum, 'action_history', finishedActionHistory || actionHistory);
        setCurrentGameData(boardNum, 'solution_length', solution_length)
        setCurrentGameData(boardNum, 'operators', operators)

        // Now it's safe to open the end modal (data persisted)
        setShowEndModal(true);

        const submitting_data = {
            user_browser_id: basicUserData.userId,
            // Full action log (not just the winning path), encoded to match the
            // existing integer-array shape: undo/clear get sentinel values since
            // they aren't operators, everything else is its operator index.
            num_history: (finishedActionHistory || actionHistory).map((action) => {
                if (action === 'U') return -1;
                if (action === 'C') return -2;
                return Number(operators.indexOf(action));
            }),
            time_taken: timeTaken,
            game_score: gameScore,
            board_num: boardNum
        };

        // await submitUserData({user_browser_id: basicUserData.userId});
        // Submit the data to the server
        await submitEventData(submitting_data);

        log("Submitting data: ", submitting_data);
    }

    playerFinishedRef.current = playerFinished;

    const clearGame = useCallback(() => {
        if (operationHistory.length === 0) {
            return; // Do nothing if there is no history
        }

        if (!resetTrackedRef.current) {
            resetTrackedRef.current = true;
            trackAnalyticsEvent('reset_used', { board_number: boardNum });
        }

        const newNumHistory = numHistory.slice(0, 1);
        const newActionHistory = [...actionHistory, 'C'];
        updateGameHistory(newNumHistory, [], [], newActionHistory);
    }, [actionHistory, boardNum, numHistory, operationHistory, updateGameHistory]);

    const handleUndoClick = useCallback(() => {
        if (operationHistory.length === 0) return;

        if (!undoTrackedRef.current) {
            undoTrackedRef.current = true;
            trackAnalyticsEvent('undo_used', { board_number: boardNum });
        }

        const newNumHistory = numHistory.slice(0, numHistory.length - 1);
        const newActionHistory = [...actionHistory, 'U'];
        updateGameHistory(
            newNumHistory,
            operationHistory.slice(0, operationHistory.length - 1),
            numPermHistory.slice(0, numPermHistory.length - 1),
            newActionHistory
        );
    }, [actionHistory, boardNum, numHistory, operationHistory, numPermHistory, updateGameHistory]);

    useEffect(() => {
        if (!gameStarted || gameDone || showHowToPlay || showEndModal || archiveMenuOpen) return undefined;

        const handleKeyDown = (event) => {
            const targetTag = event.target?.tagName?.toLowerCase();
            if (['input', 'textarea', 'select', 'button'].includes(targetTag)) return;

            const operatorIndex = ['1', '2', '3'].indexOf(event.key);
            if (operatorIndex !== -1 && operators[operatorIndex]) {
                event.preventDefault();
                setKeyboardFlash({ id: `operator-${operatorIndex}`, nonce: Date.now() });
                handleOperatorClick(operators[operatorIndex]);
            } else if (event.key === 'Backspace' || event.key === 'Delete' || event.key.toLowerCase() === 'u') {
                event.preventDefault();
                setKeyboardFlash({ id: 'undo', nonce: Date.now() });
                handleUndoClick();
            } else if (event.key === ' ' || event.key.toLowerCase() === 'c') {
                event.preventDefault();
                setKeyboardFlash({ id: 'clear', nonce: Date.now() });
                clearGame();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [archiveMenuOpen, clearGame, gameDone, gameStarted, handleOperatorClick, handleUndoClick, operators, showEndModal, showHowToPlay, operationHistory, numHistory, numPermHistory, actionHistory]);

    // Get difference of time taken when started vs time ended
    const getTimeTaken = () => {
        if (!startTimeRef.current) return 0;
        return Date.now() - startTimeRef.current;
    };

    const getGameScore = (timeTaken, newNumPermHistory) => {
        const gameScore = calculateGameScore(timeTaken, newNumPermHistory.length - limit);
        setCurrentGameData(boardNum, 'gameScore', gameScore);
        return gameScore;
    }

    // Respect persisted "show timer" preference
    const showTimerPref = useShowTimerSetting();

    return (
        <div style={{ backgroundColor: main_background_color, display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {showLimitReachedModal && (
                <LimitReachedModal
                    limit={limit}
                    remainingTargets={targetNums.length - targetProgressNum}
                    totalTargets={targetNums.length}
                    onTryAgain={() => { clearGame(); setShowLimitReachedModal(false); }}
                    onClose={() => setShowLimitReachedModal(false)}
                />
            )}
            {showConfetti && (
                <div className='confetti' aria-hidden='true'>
                    {confettiPieces.map((piece) => <span key={piece} style={{ '--confetti-delay': `${piece * 20}ms`, '--confetti-left': `${(piece * 37) % 100}%`, '--confetti-rotation': `${(piece * 47) % 360}deg` }} />)}
                </div>
            )}
            <div className='game-timer'>

                {(gameStarted || isTiming) && !gameDone && showTimerPref && (
                    <span style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)', color: 'white', display: 'inline-block', height: '2.5vh', borderRadius: '4px', padding: '0.1em 0.5em' }}>
                        {Math.floor(elapsedMs / 1000)}s
                    </span>
                )}
            </div>


            <div className='target-numbers-container'>
                {targetNums.map((target, idx) => {
                    const targetState = targetProgressNum === idx ? 'present' : targetProgressNum > idx ? 'past' : 'future';
                    return (
                        <div key={idx} className='target-numbers'>
                            {idx !== 0 && <p style={{ margin: '1em' }}>→</p>}
                            <div className={`target-number target-number-${targetState}`}>
                                <p className='target-number-number' style={{ visibility: (showHowToPlay && !gameDone) ? 'hidden' : 'visible' }}>{target}</p>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className='warning-container'>
                {warningMessage && (
                    <div className='warning-message'>
                        {warningMessage}
                    </div>
                )}
            </div>

            <div className="number-container">
                <div className="number-box" ref={scrollBoxRef}>
                    {numHistory.map((elm, idx) => (
                        <div key={idx}>
                            <p className='number-operation'
                                style={{
                                    color: 'var(--numberColorOperation)'
                                }}>{operationHistory[idx - 1]}</p>
                            <p className={`${idx === numHistory.length - 1 ? 'number-current-number' : 'number-past-number'} ${idx === numHistory.length - 1 && reachedLimit ? 'limit-reached-number' : ''}`}>{(showHowToPlay && !gameDone) ? 'X' : elm}</p>
                        </div>

                    ))}
                </div>
            </div>

            <div className='button-container' ref={buttonContainerRef}>
                <p className={`limit-text ${reachedLimit ? 'limit-reached' : ''}`}>{operationHistory.length} / {(showHowToPlay && !gameDone) ? 'X' : limit}</p>

                {!gameDone ?
                    <>
                        {!gameStarted ? (
                            showHowToPlay ? (
                                isArchived ? (
                                    <ArchivedBoardModal boardNum={boardNum} difficulty={difficulty} onStart={handleStart} onClose={() => setShowHowToPlay(false)} />
                                ) : (
                                    <HowToPlay difficulty={difficulty} isTutorial={isTutorial} onStart={handleStart} onClose={() => setShowHowToPlay(false)} />
                                )
                            ) : (
                                <div className='start-controls'>
                                    <BoardButton className='start-button' onClick={handleStart} label={'Start'}>Start</BoardButton>
                                    <BoardButton className='start-button' onClick={() => setShowHowToPlay(true)} label={'How to Play'}>How to Play</BoardButton>
                                </div>
                            )
                        ) : (
                            <>
                                <div className='operation-buttons'>
                                    {operators.map((operator, index) => (
                                        <BoardButton className={`operation-button ${keyboardFlash?.id === `operator-${index}` ? 'button-keyboard-flash' : ''}`} key={index} onClick={() => handleOperatorClick(operator)} label={(showHowToPlay && !gameDone) ? '' : formatOperatorLabel(operator)} disabled={(showHowToPlay && !gameDone)}>{(showHowToPlay && !gameDone) ? '' : formatOperatorLabel(operator)}</BoardButton>
                                    ))}
                                </div>

                                <div className='function-buttons'>
                                    {functionsButtons.map((button, index) => (
                                        <BoardButton className={`function-button ${keyboardFlash?.id === button.title.toLowerCase() ? 'button-keyboard-flash' : ''}`} key={index} onClick={button.func} label={button.title}>{button.title}</BoardButton>
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                    :
                    <>
                        <div className='game-result-buttons'>
                            {!showEndModal && previous !== null && (
                                <BoardButton className='function-button board-nav-button' onClick={() => handleBoardNavigation(previous)} label={`Play Board #${previous}`} />
                            )}
                            <BoardButton className='operation-button' onClick={() => setShowEndModal(true)} label={'Results'} />
                            {!showEndModal && next !== null && (
                                <BoardButton className='function-button board-nav-button' onClick={() => handleBoardNavigation(next)} label={`Play Board #${next}`} />
                            )}
                        </div>
                    </>
                }
            </div>

            {isMobileViewport && <div aria-hidden='true' style={{ height: buttonContainerHeight }} />}

        </div>
    );
};

export default Game;
